import { useEffect, useRef } from 'react';
import { Box, Button } from '@mui/material';

const PP = "'Poppins', sans-serif";

// Reusable signature pad. Exposes { getDataUrl, isEmpty } to the parent via the
// onReady callback (call them on submit). Shared by the public sign page (customer)
// and the sale page (sales person). No emoji per project rule.
export default function SignaturePad({ onReady }) {
    const canvasRef = useRef(null);
    const drawing = useRef(false);
    const lastPoint = useRef(null);
    const hasDrawn = useRef(false);

    const getPoint = (e, canvas) => {
        const rect = canvas.getBoundingClientRect();
        const src = e.touches ? e.touches[0] : e;
        return {
            x: (src.clientX - rect.left) * (canvas.width / rect.width),
            y: (src.clientY - rect.top) * (canvas.height / rect.height),
        };
    };

    const startDraw = (e) => {
        e.preventDefault();
        drawing.current = true;
        lastPoint.current = getPoint(e, canvasRef.current);
    };

    const draw = (e) => {
        e.preventDefault();
        if (!drawing.current) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const pt = getPoint(e, canvas);
        ctx.beginPath();
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.strokeStyle = '#0F172A';
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.stroke();
        lastPoint.current = pt;
        hasDrawn.current = true;
    };

    const endDraw = (e) => {
        e.preventDefault();
        drawing.current = false;
        lastPoint.current = null;
    };

    const handleClear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        hasDrawn.current = false;
    };

    const getDataUrl = () => {
        if (!canvasRef.current) return '';
        return canvasRef.current.toDataURL('image/png');
    };

    const isEmpty = () => !hasDrawn.current;

    useEffect(() => {
        if (onReady) onReady({ getDataUrl, isEmpty });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <canvas
                ref={canvasRef}
                width={600}
                height={200}
                style={{
                    width: '100%', height: '160px', display: 'block',
                    border: '1.5px solid #CBD5E1', borderRadius: '10px',
                    background: '#FAFAFA', touchAction: 'none', cursor: 'crosshair'
                }}
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={endDraw}
                onMouseLeave={endDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={endDraw}
            />
            <Button
                size="small"
                variant="outlined"
                onClick={handleClear}
                sx={{
                    mt: 0.75, fontFamily: PP, textTransform: 'none', fontSize: '0.75rem',
                    borderColor: '#CBD5E1', color: '#64748B', borderRadius: '8px'
                }}
            >
                Clear
            </Button>
        </Box>
    );
}
